import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getSales, reprocessSale } from '../services/api';
import { 
  Search, RefreshCw, Download, CheckCircle, XCircle, Loader2, RotateCcw, 
  Calendar, ChevronDown, Settings2, GripVertical, Eye, EyeOff, X,
  ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight
} from 'lucide-react';

const DEFAULT_COLUMNS = [
  { id: 'date', label: 'Data', width: 120, visible: true, sortable: true },
  { id: 'customer', label: 'Cliente', width: 150, visible: true, sortable: true },
  { id: 'phone', label: 'Telefone', width: 130, visible: true, sortable: false },
  { id: 'product', label: 'Produto', width: 180, visible: true, sortable: true },
  { id: 'origin', label: 'Origem', width: 100, visible: true, sortable: true },
  { id: 'seller', label: 'Vendedor', width: 130, visible: true, sortable: true },
  { id: 'status', label: 'Status', width: 110, visible: true, sortable: true },
  { id: 'utmMedium', label: 'UTM Medium', width: 150, visible: true, sortable: true },
  { id: 'value', label: 'Valor', width: 110, visible: true, sortable: true },
  { id: 'commission', label: 'Comissão', width: 110, visible: true, sortable: true },
  { id: 'tracking', label: 'Tracking', width: 90, visible: true, sortable: true },
  { id: 'meta', label: 'Meta', width: 80, visible: true, sortable: true },
  { id: 'actions', label: 'Ações', width: 80, visible: true, sortable: false },
  { id: 'utmSource', label: 'UTM Source', width: 120, visible: false, sortable: true },
  { id: 'utmCampaign', label: 'UTM Campaign', width: 150, visible: false, sortable: true },
  { id: 'utmContent', label: 'UTM Content', width: 120, visible: false, sortable: true },
  { id: 'utmTerm', label: 'UTM Term', width: 120, visible: false, sortable: true },
  { id: 'transactionId', label: 'ID Transação', width: 150, visible: false, sortable: true },
  { id: 'email', label: 'Email', width: 200, visible: false, sortable: true },
  { id: 'paymentMethod', label: 'Forma Pagamento', width: 130, visible: false, sortable: true },
  { id: 'ctwaId', label: 'FBCLID / CTWA', width: 150, visible: true, sortable: false },
];

const DATE_PRESETS = [
  { id: 'today', label: 'Hoje' },
  { id: 'yesterday', label: 'Ontem' },
  { id: 'last7', label: 'Últimos 7 dias' },
  { id: 'thisMonth', label: 'Este Mês' },
  { id: 'lastMonth', label: 'Mês Passado' },
  { id: 'custom', label: 'Personalizado' },
];

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

const Sales = () => {
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });
  const [pageSize, setPageSize] = useState(20);
  
  const [search, setSearch] = useState('');
  const [datePreset, setDatePreset] = useState('last7');
  const [customDateRange, setCustomDateRange] = useState({ start: '', end: '' });
  const [filters, setFilters] = useState({
    product: '',
    utmMedium: '',
    trackingFound: '',
    metaEventSent: '',
    origin: '',
    seller: '',
    status: ''
  });
  
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showColumnSettings, setShowColumnSettings] = useState(false);
  const [columns, setColumns] = useState(() => {
    const saved = localStorage.getItem('jtracker_sales_columns');
    if (!saved) return DEFAULT_COLUMNS;
    const parsed = JSON.parse(saved);
    // Mesclar: manter ordem/visibilidade salva, adicionar colunas novas
    const savedIds = parsed.map(c => c.id);
    const newCols = DEFAULT_COLUMNS.filter(c => !savedIds.includes(c.id));
    if (newCols.length > 0) {
      // Inserir novas colunas na posição original
      const merged = [...parsed];
      newCols.forEach(nc => {
        const origIdx = DEFAULT_COLUMNS.findIndex(c => c.id === nc.id);
        merged.splice(Math.min(origIdx, merged.length), 0, nc);
      });
      return merged;
    }
    return parsed;
  });
  
  const [draggedColumn, setDraggedColumn] = useState(null);
  const [resizingColumn, setResizingColumn] = useState(null);
  const [resizeStartX, setResizeStartX] = useState(0);
  const [resizeStartWidth, setResizeStartWidth] = useState(0);
  
  const [filterOptions, setFilterOptions] = useState({
    products: [],
    utmMediums: [],
    origins: [],
    sellers: [],
    statuses: []
  });

  const tableRef = useRef(null);
  const datePickerRef = useRef(null);
  const columnSettingsRef = useRef(null);

  const getDateRange = useCallback(() => {
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    const formatDate = (d) => d.toISOString().split('T')[0];
    
    switch (datePreset) {
      case 'today':
        return { startDate: formatDate(today), endDate: formatDate(today) };
      case 'yesterday':
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        return { startDate: formatDate(yesterday), endDate: formatDate(yesterday) };
      case 'last7':
        const last7 = new Date(today);
        last7.setDate(last7.getDate() - 6);
        return { startDate: formatDate(last7), endDate: formatDate(today) };
      case 'thisMonth':
        const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
        return { startDate: formatDate(firstDay), endDate: formatDate(today) };
      case 'lastMonth':
        const firstLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const lastLastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
        return { startDate: formatDate(firstLastMonth), endDate: formatDate(lastLastMonth) };
      case 'custom':
        return { startDate: customDateRange.start, endDate: customDateRange.end };
      default:
        return { startDate: '', endDate: '' };
    }
  }, [datePreset, customDateRange]);

  const fetchSales = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const dateRange = getDateRange();
      const params = { 
        page, 
        limit: pageSize, 
        search,
        ...dateRange,
        ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v !== ''))
      };
      
      const response = await getSales(params);
      setSales(response.data.sales || []);
      setPagination({
        page: response.data.pagination?.page || 1,
        totalPages: response.data.pagination?.totalPages || 1,
        total: response.data.pagination?.total || 0
      });
      
      if (response.data.filterOptions) {
        setFilterOptions(response.data.filterOptions);
      }
    } catch (error) {
      console.error('Erro ao buscar vendas:', error);
    } finally {
      setLoading(false);
    }
  }, [pageSize, search, filters, getDateRange]);

  useEffect(() => { fetchSales(1); }, [datePreset, customDateRange, filters, pageSize]);
  useEffect(() => { localStorage.setItem('jtracker_sales_columns', JSON.stringify(columns)); }, [columns]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (datePickerRef.current && !datePickerRef.current.contains(e.target)) setShowDatePicker(false);
      if (columnSettingsRef.current && !columnSettingsRef.current.contains(e.target)) setShowColumnSettings(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearch = (e) => { e.preventDefault(); fetchSales(1); };
  const handleReprocess = async (id) => { try { await reprocessSale(id); fetchSales(pagination.page); } catch (e) { console.error('Erro ao reprocessar:', e); } };

  const handleExport = () => {
    const visibleCols = columns.filter(c => c.visible && c.id !== 'actions');
    const headers = visibleCols.map(c => c.label).join(',');
    const rows = sales.map(sale => 
      visibleCols.map(col => {
        const value = getCellValue(sale, col.id);
        return `"${String(value).replace(/"/g, '""')}"`;
      }).join(',')
    );
    const csv = [headers, ...rows].join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `vendas_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const handleDragStart = (e, columnId) => { setDraggedColumn(columnId); e.dataTransfer.effectAllowed = 'move'; };
  const handleDragOver = (e, columnId) => {
    e.preventDefault();
    if (draggedColumn && draggedColumn !== columnId) {
      const newColumns = [...columns];
      const dragIndex = newColumns.findIndex(c => c.id === draggedColumn);
      const dropIndex = newColumns.findIndex(c => c.id === columnId);
      const [removed] = newColumns.splice(dragIndex, 1);
      newColumns.splice(dropIndex, 0, removed);
      setColumns(newColumns);
    }
  };
  const handleDragEnd = () => { setDraggedColumn(null); };

  const handleResizeStart = (e, columnId) => {
    e.preventDefault();
    setResizingColumn(columnId);
    setResizeStartX(e.clientX);
    const col = columns.find(c => c.id === columnId);
    setResizeStartWidth(col?.width || 100);
  };

  useEffect(() => {
    if (!resizingColumn) return;
    const handleMouseMove = (e) => {
      const diff = e.clientX - resizeStartX;
      const newWidth = Math.max(60, resizeStartWidth + diff);
      setColumns(cols => cols.map(c => c.id === resizingColumn ? { ...c, width: newWidth } : c));
    };
    const handleMouseUp = () => { setResizingColumn(null); };
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => { document.removeEventListener('mousemove', handleMouseMove); document.removeEventListener('mouseup', handleMouseUp); };
  }, [resizingColumn, resizeStartX, resizeStartWidth]);

  const toggleColumn = (columnId) => { setColumns(cols => cols.map(c => c.id === columnId ? { ...c, visible: !c.visible } : c)); };
  const resetColumns = () => { setColumns(DEFAULT_COLUMNS); };

  const getCellValue = (sale, columnId) => {
    switch (columnId) {
      case 'date': return new Date(sale.createdAt).toLocaleDateString('pt-BR');
      case 'customer': {
        const name = sale.customerName || '-';
        return name === '-' ? '-' : name.toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
      }
      case 'phone': return sale.customerPhone || '-';
      case 'product': return sale.productName || '-';
      case 'origin': return sale.origin || sale.platform || '-';
      case 'utmMedium': return sale.utmMedium || '-';
      case 'value': return `R$ ${parseFloat(sale.value || 0).toFixed(2)}`;
      case 'commission': return `R$ ${parseFloat(sale.producerCommission || 0).toFixed(2)}`;
      case 'tracking': return sale.trackingFound;
      case 'meta': return sale.metaEventSent;
      case 'utmSource': return sale.utmSource || '-';
      case 'utmCampaign': return sale.utmCampaign || '-';
      case 'utmContent': return sale.utmContent || '-';
      case 'utmTerm': return sale.utmTerm || '-';
      case 'transactionId': return sale.transactionId || '-';
      case 'email': return sale.customerEmail || '-';
      case 'seller': return sale.sellerName || '-';
      case 'status': return sale.paymentStatus || sale.status || '-';
      case 'paymentMethod': return sale.paymentMethod || '-';
      case 'ctwaId': return sale.ctwaId ? <span title={sale.ctwaId} className="font-mono text-xs">{sale.ctwaId.length > 18 ? sale.ctwaId.substring(0, 18) + '...' : sale.ctwaId}</span> : '-';
      default: return '-';
    }
  };

  const renderCell = (sale, columnId) => {
    const value = getCellValue(sale, columnId);
    switch (columnId) {
      case 'date':
        return (<div><span className="text-white text-sm">{value}</span><br/><span className="text-gray-500 text-xs">{new Date(sale.createdAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</span></div>);
      case 'customer':
        return <span className="text-white font-medium truncate block" title={value}>{value}</span>;
      case 'phone':
        return <span className="text-gray-300 text-sm">{value}</span>;
      case 'product':
        return <span className="text-gray-300 text-sm truncate block" title={value}>{value}</span>;
      case 'origin':
        return (<span className={`px-2 py-1 rounded text-xs font-medium ${value === 'Payt' ? 'bg-purple-500/20 text-purple-400' : value === 'Braip' ? 'bg-blue-500/20 text-blue-400' : value === 'Kiwify' ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'}`}>{value}</span>);
      case 'utmMedium':
        return <span className="text-gray-400 text-xs truncate block" title={value}>{value}</span>;
      case 'value':
        return <span className="text-green-400 font-medium">{value}</span>;
      case 'commission':
        return <span className="text-blue-400 font-medium">{value}</span>;
      case 'tracking':
        return (<div className="flex justify-center">{value ? <CheckCircle className="text-green-400" size={20} /> : <XCircle className="text-red-400" size={20} />}</div>);
      case 'meta':
        return (<div className="flex justify-center">{value ? <CheckCircle className="text-green-400" size={20} /> : <XCircle className="text-yellow-400" size={20} />}</div>);
      case 'seller':
        return <span className={value !== '-' ? 'text-cyan-400 font-medium text-sm' : 'text-gray-500 text-sm'}>{value}</span>;
      case 'status':
        const statusMap = { 
          approved: { label: 'Aprovado', cls: 'bg-green-900/50 text-green-400' }, 
          refunded: { label: 'Reembolsado', cls: 'bg-orange-900/50 text-orange-400' }, 
          chargeback: { label: 'Chargeback', cls: 'bg-red-900/50 text-red-400' }, 
          waiting_payment: { label: 'Aguardando', cls: 'bg-yellow-900/50 text-yellow-400' }, 
          canceled: { label: 'Cancelado', cls: 'bg-gray-700 text-gray-400' },
          cancelled: { label: 'Cancelado', cls: 'bg-gray-700 text-gray-400' },
          refund: { label: 'Reembolsado', cls: 'bg-orange-900/50 text-orange-400' },
          card_refused: { label: 'Recusado', cls: 'bg-red-900/50 text-red-400' },
          abandoned: { label: 'Abandonado', cls: 'bg-yellow-900/50 text-yellow-400' },
          boleto_expired: { label: 'Boleto Vencido', cls: 'bg-orange-900/50 text-orange-400' },
        };
        const st = statusMap[value] || { label: value, cls: 'bg-gray-700 text-gray-300' };
        return <span className={`px-2 py-1 rounded-full text-xs font-medium ${st.cls}`}>{st.label}</span>;
      case 'actions':
        return (<div className="flex justify-center"><button onClick={() => handleReprocess(sale.id)} className="p-2 text-gray-400 hover:text-blue-400 hover:bg-blue-500/10 rounded transition-colors" title="Reprocessar"><RotateCcw size={18} /></button></div>);
      default:
        return <span className="text-gray-400 text-sm truncate block">{value}</span>;
    }
  };

  const visibleColumns = columns.filter(c => c.visible);

  const getDateLabel = () => {
    const preset = DATE_PRESETS.find(p => p.id === datePreset);
    if (datePreset === 'custom' && customDateRange.start && customDateRange.end) {
      const start = new Date(customDateRange.start + 'T12:00:00').toLocaleDateString('pt-BR');
      const end = new Date(customDateRange.end + 'T12:00:00').toLocaleDateString('pt-BR');
      return `${start} - ${end}`;
    }
    return preset?.label || 'Selecione';
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Vendas</h1>
          <p className="text-gray-400">{pagination.total} vendas no período selecionado</p>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => setShowColumnSettings(true)} className="flex items-center gap-2 bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded-lg transition-colors"><Settings2 size={20} />Colunas</button>
          <button onClick={handleExport} className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors"><Download size={20} />Exportar</button>
        </div>
      </div>

      <div className="bg-gray-800 rounded-xl p-4">
        <form onSubmit={handleSearch} className="space-y-4">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px] relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
              <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar por cliente, telefone, produto..." className="w-full bg-gray-900 border border-gray-700 rounded-lg pl-10 pr-4 py-2 text-white focus:border-blue-500 focus:outline-none transition-colors" />
            </div>

            <div className="relative" ref={datePickerRef}>
              <button type="button" onClick={() => setShowDatePicker(!showDatePicker)} className="flex items-center gap-2 bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white hover:border-gray-600 transition-colors min-w-[200px]">
                <Calendar size={18} className="text-gray-400" />
                <span className="flex-1 text-left">{getDateLabel()}</span>
                <ChevronDown size={18} className={`text-gray-400 transition-transform ${showDatePicker ? 'rotate-180' : ''}`} />
              </button>

              {showDatePicker && (
                <div className="absolute top-full left-0 mt-2 bg-gray-800 border border-gray-700 rounded-xl shadow-xl z-50 min-w-[280px]">
                  <div className="p-2">
                    {DATE_PRESETS.map(preset => (
                      <button key={preset.id} type="button" onClick={() => { setDatePreset(preset.id); if (preset.id !== 'custom') setShowDatePicker(false); }} className={`w-full text-left px-4 py-2 rounded-lg transition-colors ${datePreset === preset.id ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-700'}`}>{preset.label}</button>
                    ))}
                  </div>
                  {datePreset === 'custom' && (
                    <div className="border-t border-gray-700 p-4 space-y-3">
                      <div><label className="text-gray-400 text-sm block mb-1">Data Inicial</label><input type="date" value={customDateRange.start} onChange={(e) => setCustomDateRange(prev => ({ ...prev, start: e.target.value }))} className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white" /></div>
                      <div><label className="text-gray-400 text-sm block mb-1">Data Final</label><input type="date" value={customDateRange.end} onChange={(e) => setCustomDateRange(prev => ({ ...prev, end: e.target.value }))} className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white" /></div>
                      <button type="button" onClick={() => setShowDatePicker(false)} className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 rounded-lg transition-colors">Aplicar</button>
                    </div>
                  )}
                </div>
              )}
            </div>

            <button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors">Buscar</button>
            <button type="button" onClick={() => fetchSales(1)} className="p-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-gray-400 hover:text-white transition-colors" title="Atualizar"><RefreshCw size={20} /></button>
          </div>

          <div className="flex flex-wrap gap-3">
            <select value={filters.product} onChange={(e) => setFilters(prev => ({ ...prev, product: e.target.value }))} className="bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm min-w-[150px]">
              <option value="">Todos Produtos</option>
              {filterOptions.products.map(p => (<option key={p} value={p}>{p}</option>))}
            </select>
            <select value={filters.origin} onChange={(e) => setFilters(prev => ({ ...prev, origin: e.target.value }))} className="bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm min-w-[120px]">
              <option value="">Todas Origens</option>
              <option value="Payt">Payt</option>
              <option value="Braip">Braip</option>
              <option value="Kiwify">Kiwify</option>
            </select>
            <select value={filters.utmMedium} onChange={(e) => setFilters(prev => ({ ...prev, utmMedium: e.target.value }))} className="bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm min-w-[150px]">
              <option value="">Todos UTM Medium</option>
              {filterOptions.utmMediums.map(u => (<option key={u} value={u}>{u}</option>))}
            </select>
            <select value={filters.trackingFound} onChange={(e) => setFilters(prev => ({ ...prev, trackingFound: e.target.value }))} className="bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm min-w-[130px]">
              <option value="">Tracking: Todos</option>
              <option value="true">Com Tracking</option>
              <option value="false">Sem Tracking</option>
            </select>
            <select value={filters.metaEventSent} onChange={(e) => setFilters(prev => ({ ...prev, metaEventSent: e.target.value }))} className="bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm min-w-[130px]">
              <option value="">Meta: Todos</option>
              <option value="true">Enviado</option>
              <option value="false">Não Enviado</option>
            </select>
            <select value={filters.seller} onChange={(e) => setFilters(prev => ({ ...prev, seller: e.target.value }))} className="bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm min-w-[130px]">
              <option value="">Vendedor: Todos</option>
              {filterOptions.sellers.map(s => (<option key={s} value={s}>{s}</option>))}
            </select>
            <select value={filters.status} onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))} className="bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm min-w-[130px]">
              <option value="">Status: Todos</option>
              {[
                { value: 'approved', label: 'Aprovado' },
                { value: 'canceled', label: 'Cancelado' },
                { value: 'Cancelada', label: 'Cancelada' },
                { value: 'refunded', label: 'Reembolsado' },
                { value: 'chargeback', label: 'Chargeback' },
                { value: 'waiting_payment', label: 'Aguardando Pagamento' },
                { value: 'card_refused', label: 'Cartão Recusado' },
                { value: 'abandoned', label: 'Abandonado' },
                { value: 'boleto_expired', label: 'Boleto Vencido' },
              ].map(s => (<option key={s.value} value={s.value}>{s.label}</option>))}
            </select>
            {Object.values(filters).some(v => v !== '') && (
              <button type="button" onClick={() => setFilters({ product: '', utmMedium: '', trackingFound: '', metaEventSent: '', origin: '' })} className="flex items-center gap-1 text-gray-400 hover:text-white text-sm px-3 py-2 rounded-lg hover:bg-gray-700 transition-colors"><X size={16} />Limpar Filtros</button>
            )}
          </div>
        </form>
      </div>

      <div className="bg-gray-800 rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin text-blue-500" size={40} /></div>
        ) : (
          <>
            <div className="overflow-x-auto" ref={tableRef}>
              <table className="w-full" style={{ minWidth: visibleColumns.reduce((acc, col) => acc + col.width, 0) }}>
                <thead>
                  <tr className="text-left text-gray-400 text-sm border-b border-gray-700 bg-gray-900/50">
                    {visibleColumns.map((column) => (
                      <th key={column.id} className="px-4 py-3 relative select-none group" style={{ width: column.width, minWidth: column.width }} draggable onDragStart={(e) => handleDragStart(e, column.id)} onDragOver={(e) => handleDragOver(e, column.id)} onDragEnd={handleDragEnd}>
                        <div className="flex items-center gap-2">
                          <GripVertical size={14} className="text-gray-600 opacity-0 group-hover:opacity-100 cursor-grab transition-opacity" />
                          <span className={column.id === 'tracking' || column.id === 'meta' || column.id === 'actions' ? 'w-full text-center' : ''}>{column.label}</span>
                        </div>
                        <div className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-blue-500 transition-colors" onMouseDown={(e) => handleResizeStart(e, column.id)} />
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sales.length === 0 ? (
                    <tr><td colSpan={visibleColumns.length} className="px-4 py-20 text-center text-gray-500">Nenhuma venda encontrada no período</td></tr>
                  ) : (
                    sales.map((sale) => (
                      <tr key={sale.id} className="border-b border-gray-700/50 hover:bg-gray-700/30 transition-colors">
                        {visibleColumns.map((column) => (
                          <td key={column.id} className="px-4 py-3" style={{ width: column.width, minWidth: column.width, maxWidth: column.width }}>{renderCell(sale, column.id)}</td>
                        ))}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-700 bg-gray-900/30">
              <div className="flex items-center gap-4">
                <span className="text-gray-400 text-sm">Mostrando {sales.length} de {pagination.total} vendas</span>
                <div className="flex items-center gap-2">
                  <span className="text-gray-500 text-sm">Por página:</span>
                  <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))} className="bg-gray-800 border border-gray-700 rounded px-2 py-1 text-white text-sm">
                    {PAGE_SIZE_OPTIONS.map(size => (<option key={size} value={size}>{size}</option>))}
                  </select>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-gray-400 text-sm mr-2">Página {pagination.page} de {pagination.totalPages}</span>
                <button onClick={() => fetchSales(1)} disabled={pagination.page <= 1} className="p-2 bg-gray-700 hover:bg-gray-600 rounded text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors" title="Primeira página"><ChevronsLeft size={18} /></button>
                <button onClick={() => fetchSales(pagination.page - 1)} disabled={pagination.page <= 1} className="p-2 bg-gray-700 hover:bg-gray-600 rounded text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors" title="Página anterior"><ChevronLeft size={18} /></button>
                <button onClick={() => fetchSales(pagination.page + 1)} disabled={pagination.page >= pagination.totalPages} className="p-2 bg-gray-700 hover:bg-gray-600 rounded text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors" title="Próxima página"><ChevronRight size={18} /></button>
                <button onClick={() => fetchSales(pagination.totalPages)} disabled={pagination.page >= pagination.totalPages} className="p-2 bg-gray-700 hover:bg-gray-600 rounded text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors" title="Última página"><ChevronsRight size={18} /></button>
              </div>
            </div>
          </>
        )}
      </div>

      {showColumnSettings && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div ref={columnSettingsRef} className="bg-gray-800 rounded-xl shadow-2xl w-full max-w-md max-h-[80vh] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-gray-700">
              <h3 className="text-lg font-semibold text-white">Configurar Colunas</h3>
              <button onClick={() => setShowColumnSettings(false)} className="p-1 text-gray-400 hover:text-white transition-colors"><X size={20} /></button>
            </div>
            <div className="p-4 overflow-y-auto max-h-[60vh]">
              <p className="text-gray-400 text-sm mb-4">Arraste para reordenar - Clique para mostrar/ocultar</p>
              <div className="space-y-2">
                {columns.map((column) => (
                  <div key={column.id} className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${column.visible ? 'bg-gray-700' : 'bg-gray-900'}`} onClick={() => toggleColumn(column.id)}>
                    <GripVertical size={16} className="text-gray-500" />
                    {column.visible ? <Eye size={18} className="text-green-400" /> : <EyeOff size={18} className="text-gray-500" />}
                    <span className={column.visible ? 'text-white' : 'text-gray-500'}>{column.label}</span>
                    <span className="ml-auto text-gray-500 text-xs">{column.width}px</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex items-center justify-between p-4 border-t border-gray-700">
              <button onClick={resetColumns} className="text-gray-400 hover:text-white text-sm transition-colors">Restaurar Padrão</button>
              <button onClick={() => setShowColumnSettings(false)} className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors">Feito</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Sales;
